import { useState, useEffect } from 'react';
import { CheckCircle, XCircle } from '@phosphor-icons/react';
import { Card, CardHeader, CardTitle, CardDescription, CardContent, Button, Avatar, Switch } from '../../components/ui';
import { motion } from 'framer-motion';
import { doc, updateDoc } from 'firebase/firestore';
import { httpsCallable } from 'firebase/functions';
import { db, functions } from '@/core/firebase';
import { useAuth } from '@/core/AuthContext';
import type { GoalType, EquipmentType } from '../../types';
import { GOALS, EXPERIENCE_LEVELS, EQUIPMENT_OPTIONS, DURATIONS } from '../../lib/fitnessConstants';
import type { ExperienceLevel } from '../../lib/fitnessConstants';
import { addTrainerContact } from '../../lib/contacts';
import { useTrainerDetails } from '@/lib/useTrainerDetails';

export function TrainerSettingsSection() {
  const { userData } = useAuth();
  const trainerDetails = useTrainerDetails({
    trainerId: userData?.trainerId || userData?.trainerProfileId,
    aiTrainerId: userData?.aiTrainerId,
    enabled: Boolean(userData),
  });

  // Fitness preferences state
  const [fitnessGoal, setFitnessGoal] = useState<GoalType | null>(null);
  const [experienceLevel, setExperienceLevel] = useState<ExperienceLevel | null>(null);
  const [equipment, setEquipment] = useState<EquipmentType[]>([]);
  const [preferredDuration, setPreferredDuration] = useState<30 | 45 | 60 | null>(null);
  const [limitations, setLimitations] = useState('');
  const [fitnessSaving, setFitnessSaving] = useState(false);

  // Call preferences state
  const [callsOptedOut, setCallsOptedOut] = useState(false);
  const [callsSaving, setCallsSaving] = useState(false);

  // Trainer contact state
  const [contactStatus, setContactStatus] = useState<'idle' | 'adding' | 'added' | 'denied' | 'error'>('idle');

  // Test call state
  const [testCallLoading, setTestCallLoading] = useState(false);
  const [testCallError, setTestCallError] = useState<string | null>(null);
  const [testCallSuccess, setTestCallSuccess] = useState(false);

  useEffect(() => {
    if (!userData) return;
    setCallsOptedOut(userData.callRemindersOptedOut || false);
    setFitnessGoal(userData.fitnessGoal || null);
    setExperienceLevel(userData.experienceLevel || null);
    setEquipment(userData.equipment || []);
    setPreferredDuration(userData.preferredDuration || null);
    setLimitations(userData.limitations || '');
  }, [userData]);

  const toggleEquipment = (value: EquipmentType) => {
    setEquipment((prev) => {
      if (prev.includes(value)) {
        return prev.filter((e) => e !== value);
      } else {
        return [...prev, value];
      }
    });
  };

  const handleCallsOptOutChange = async (checked: boolean) => {
    if (!userData) return;
    setCallsSaving(true);
    setCallsOptedOut(checked);
    try {
      await updateDoc(doc(db, 'users', userData.id), {
        callRemindersOptedOut: checked,
      });
    } catch (error) {
      console.error('Failed to update call preferences:', error);
      setCallsOptedOut(!checked);
    } finally {
      setCallsSaving(false);
    }
  };

  const handleSaveFitnessPreferences = async () => {
    if (!userData) return;
    setFitnessSaving(true);
    try {
      await updateDoc(doc(db, 'users', userData.id), {
        fitnessGoal,
        experienceLevel,
        equipment,
        preferredDuration,
        limitations,
      });
    } catch (error) {
      console.error('Failed to save fitness preferences:', error);
      alert('Failed to save preferences. Please try again.');
    } finally {
      setFitnessSaving(false);
    }
  };

  const handleAddTrainerContact = async () => {
    if (!trainerDetails.name) return;

    setContactStatus('adding');
    try {
      const result = await addTrainerContact({
        name: trainerDetails.name || 'Your Trainer',
        phone: trainerDetails.phone,
        email: trainerDetails.email,
        company: trainerDetails.company,
        persona: trainerDetails.persona,
        photoURL: trainerDetails.photoURL,
        aiTrainerId: userData?.aiTrainerId,
      });

      setContactStatus(result === 'denied' ? 'denied' : 'added');
      if (result !== 'denied') {
        setTimeout(() => setContactStatus('idle'), 3000);
      }
    } catch (error) {
      console.error('Failed to add trainer contact:', error);
      setContactStatus('error');
      setTimeout(() => setContactStatus('idle'), 3000);
    }
  };

  const handleTestCall = async () => {
    if (!userData) return;

    setTestCallLoading(true);
    setTestCallError(null);
    setTestCallSuccess(false);

    try {
      const sendCallReminder = httpsCallable(functions, 'sendCallReminder');
      const result = await sendCallReminder({
        userId: userData.id,
        sessionId: 'test-call',
        message: `Hi ${userData.displayName || 'there'}! This is a test call from your trainer at Start Grounded. Your phone is set up correctly for workout reminders. Have a great day!`,
      });

      console.log('Test call result:', result.data);
      setTestCallSuccess(true);

      setTimeout(() => {
        setTestCallSuccess(false);
      }, 5000);
    } catch (error: any) {
      console.error('Failed to trigger test call:', error);
      const errorMessage = error?.message || error?.code || 'Failed to initiate call';
      setTestCallError(errorMessage);
    } finally {
      setTestCallLoading(false);
    }
  };

  return (
    <>
      {/* Trainer Contact */}
      {!trainerDetails.loading && trainerDetails.name && (
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.45 }}
          className="order-[-20]"
        >
          <Card variant="elevated" padding="lg">
            <CardHeader>
              <CardTitle>Your Trainer</CardTitle>
              <CardDescription>{trainerDetails.label}</CardDescription>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                <div className="flex items-center gap-4">
                  <Avatar
                    src={trainerDetails.photoURL}
                    alt={trainerDetails.name}
                    fallback={trainerDetails.name}
                    size="xl"
                    className="w-16 h-16"
                  />
                  <div className="flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
                      {trainerDetails.name}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-slate-400">
                      {trainerDetails.label}
                    </p>
                  </div>
                </div>

                {trainerDetails.persona && (
                  <div className="bg-primary-50 dark:bg-primary-500/10 rounded-lg p-3">
                    <p className="text-sm text-primary-900 dark:text-primary-300 italic">
                      "{trainerDetails.persona}"
                    </p>
                  </div>
                )}

                <div className="space-y-3 pt-2 border-t border-gray-200 dark:border-white/10">
                  <div className="flex items-center gap-3">
                    <div className="p-2 rounded-lg bg-primary-50 dark:bg-primary-500/10">
                      <svg
                        className="w-5 h-5 text-primary-600 dark:text-primary-400"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                        />
                      </svg>
                    </div>
                    <div className="flex-1">
                      <p className="text-xs text-gray-500 dark:text-slate-500">Calls from</p>
                      <p className="text-sm font-semibold text-gray-900 dark:text-white">
                        {trainerDetails.phone || 'Not available'}
                      </p>
                    </div>
                  </div>
                </div>

                <div className="pt-2 space-y-3">
                  <div className="flex flex-col sm:flex-row gap-3">
                    <Button
                      variant="secondary"
                      onClick={handleAddTrainerContact}
                      disabled={contactStatus === 'adding' || contactStatus === 'added'}
                      isLoading={contactStatus === 'adding'}
                      className="w-full sm:w-auto"
                    >
                      <svg
                        className="w-4 h-4 mr-2"
                        fill="none"
                        viewBox="0 0 24 24"
                        stroke="currentColor"
                      >
                        {contactStatus === 'added' ? (
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        ) : (
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"
                          />
                        )}
                      </svg>
                      {contactStatus === 'added' ? 'Contact Added' : 'Add to Contacts'}
                    </Button>

                    {trainerDetails.phone && (
                      <Button
                        variant="primary"
                        onClick={handleTestCall}
                        isLoading={testCallLoading}
                        disabled={testCallLoading || callsOptedOut}
                        className="w-full sm:w-auto"
                      >
                        <svg
                          className="w-4 h-4 mr-2"
                          fill="none"
                          viewBox="0 0 24 24"
                          stroke="currentColor"
                        >
                          <path
                            strokeLinecap="round"
                            strokeLinejoin="round"
                            strokeWidth={2}
                            d="M3 5a2 2 0 012-2h3.28a1 1 0 01.948.684l1.498 4.493a1 1 0 01-.502 1.21l-2.257 1.13a11.042 11.042 0 005.516 5.516l1.13-2.257a1 1 0 011.21-.502l4.493 1.498a1 1 0 01.684.949V19a2 2 0 01-2 2h-1C9.716 21 3 14.284 3 6V5z"
                          />
                        </svg>
                        Call Me Now
                      </Button>
                    )}
                  </div>

                  <p className="text-xs text-gray-500 dark:text-slate-500">
                    Save your trainer to contacts so you know when they're calling
                  </p>

                  {callsOptedOut && (
                    <p className="text-xs text-yellow-600 dark:text-yellow-400">
                      Calls are disabled because you've opted out of reminder calls
                    </p>
                  )}

                  {contactStatus === 'denied' && (
                    <div className="bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg p-3">
                      <p className="text-sm text-amber-700 dark:text-amber-300">
                        Contacts access was denied. Open iOS Settings to grant permission.
                      </p>
                    </div>
                  )}

                  {contactStatus === 'error' && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        Failed to add contact. Please try again.
                      </p>
                    </div>
                  )}

                  {testCallSuccess && (
                    <div className="bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800 rounded-lg p-3">
                      <p className="text-sm text-success-700 dark:text-success-300">
                        <span className="inline-flex items-center gap-2">
                          <CheckCircle size={16} weight="bold" />
                          Test call initiated! You should receive a call shortly.
                        </span>
                      </p>
                    </div>
                  )}

                  {testCallError && (
                    <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg p-3">
                      <p className="text-sm text-red-700 dark:text-red-300">
                        <span className="inline-flex items-center gap-2">
                          <XCircle size={16} weight="bold" />
                          Failed to initiate call: {testCallError}
                        </span>
                      </p>
                    </div>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        </motion.div>
      )}

      {/* Call Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.4 }}
      >
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <CardTitle>Call Preferences</CardTitle>
            <CardDescription>Manage trainer workout reminder calls</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {userData?.phone && (
                <div>
                  <p className="text-sm font-medium text-gray-700 dark:text-slate-300 mb-1">
                    Phone Number
                  </p>
                  <p className="text-sm text-gray-900 dark:text-white font-mono">
                    {userData.phone}
                  </p>
                </div>
              )}

              <div className="flex items-start justify-between gap-4">
                <div className="flex-1">
                  <p className="text-sm font-medium text-gray-900 dark:text-white mb-1">
                    Disable Trainer Reminder Calls
                  </p>
                  <p className="text-xs text-gray-600 dark:text-slate-400">
                    Turn off phone call reminders and check-ins from your trainer
                  </p>
                </div>
                <Switch
                  checked={callsOptedOut}
                  onChange={(e) => handleCallsOptOutChange(e.target.checked)}
                  disabled={callsSaving}
                />
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>

      {/* Fitness Preferences */}
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.35 }}
      >
        <Card variant="elevated" padding="lg">
          <CardHeader>
            <CardTitle>Fitness Preferences</CardTitle>
            <CardDescription>Your workout goals and training preferences</CardDescription>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                  Primary Goal
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {GOALS.map((goal) => (
                    (() => {
                      const Icon = goal.icon;
                      return (
                        <button
                          key={goal.value}
                          onClick={() => setFitnessGoal(goal.value)}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            fitnessGoal === goal.value
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                          }`}
                        >
                          <div className="mb-1 text-gray-900 dark:text-white">
                            <Icon size={24} weight="bold" />
                          </div>
                          <div className="text-xs font-medium text-gray-900 dark:text-white">
                            {goal.label}
                          </div>
                        </button>
                      );
                    })()
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                  Experience Level
                </label>
                <div className="space-y-2">
                  {EXPERIENCE_LEVELS.map((level) => (
                    <button
                      key={level.value}
                      onClick={() => setExperienceLevel(level.value)}
                      className={`w-full p-3 rounded-lg border transition-all text-left ${
                        experienceLevel === level.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                          : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                      }`}
                    >
                      <div className="font-medium text-sm text-gray-900 dark:text-white">
                        {level.label}
                      </div>
                      <div className="text-xs text-gray-600 dark:text-slate-400">
                        {level.description}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                  Available Equipment
                  <span className="text-xs text-gray-500 dark:text-slate-500 ml-2">(select all that apply)</span>
                </label>
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-2">
                  {EQUIPMENT_OPTIONS.map((equipmentOption) => (
                    (() => {
                      const Icon = equipmentOption.icon;
                      return (
                        <button
                          key={equipmentOption.value}
                          onClick={() => toggleEquipment(equipmentOption.value)}
                          className={`p-3 rounded-lg border transition-all text-left ${
                            equipment.includes(equipmentOption.value)
                              ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20'
                              : 'border-gray-200 dark:border-white/10 hover:border-primary-300 dark:hover:border-primary-700'
                          }`}
                        >
                          <div className="mb-1 text-gray-900 dark:text-white">
                            <Icon size={24} weight="bold" />
                          </div>
                          <div className="text-xs font-medium text-gray-900 dark:text-white">
                            {equipmentOption.label}
                          </div>
                        </button>
                      );
                    })()
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-3">
                  Preferred Workout Duration
                </label>
                <div className="flex gap-3">
                  {DURATIONS.map((duration) => (
                    <button
                      key={duration.value}
                      onClick={() => setPreferredDuration(duration.value)}
                      className={`flex-1 px-4 py-3 rounded-lg border transition-all text-center ${
                        preferredDuration === duration.value
                          ? 'border-primary-500 bg-primary-50 dark:bg-primary-900/20 text-primary-700 dark:text-primary-400'
                          : 'border-gray-200 dark:border-white/10 text-gray-700 dark:text-slate-300 hover:bg-gray-50 dark:hover:bg-white/5'
                      }`}
                    >
                      <div className="font-bold text-lg">{duration.label}</div>
                    </button>
                  ))}
                </div>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-slate-300 mb-2">
                  Injuries or Limitations
                  <span className="text-xs text-gray-500 dark:text-slate-500 ml-2">(optional)</span>
                </label>
                <textarea
                  value={limitations}
                  onChange={(e) => setLimitations(e.target.value)}
                  placeholder="E.g., bad knee, shoulder injury, lower back issues..."
                  rows={3}
                  data-testid="limitations-input"
                  className="w-full px-4 py-3 rounded-lg border border-gray-300 dark:border-white/20 bg-white dark:bg-white/5 text-gray-900 dark:text-white placeholder-gray-400 dark:placeholder-slate-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition resize-none"
                />
              </div>

              <div className="pt-4">
                <Button
                  onClick={handleSaveFitnessPreferences}
                  isLoading={fitnessSaving}
                  disabled={fitnessSaving}
                  data-testid="save-fitness-preferences-button"
                >
                  Save Fitness Preferences
                </Button>
              </div>
            </div>
          </CardContent>
        </Card>
      </motion.div>
    </>
  );
}
